import type {
  ClientGroupChangeRequest,
  ClientGroupCreateRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import {
  ClientGroupAccessibilityEnum,
  ClientGroupCreateRequestGroupContentVisibilityEnum,
  ClientGroupCreateRequestGroupVisibilityEnum
} from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { Button, Portal, Text, TextInput } from 'react-native-paper';
import type { EdgeInsets } from 'react-native-safe-area-context';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetGroup } from '@/features/api/hooks/grapevine-group/useGetGroup';
import { useChangeGroup } from '@/features/api/hooks/grapevine-group-events/useChangeGroup';
import { useCreateGroup } from '@/features/api/hooks/grapevine-group-events/useCreateGroup';
import { useDeleteGroup } from '@/features/api/hooks/grapevine-group-events/useDeleteGroup';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { useGetSelectedGeoAreas } from '@/features/api/hooks/participant-geo-area/useGetSelectedGeoAreas';
import { useFeature } from '@/features/feature-management/provider/useFeature';
import { FeaturesEnum } from '@/features/feature-management/types/features';
import { GeoAreaSubscription } from '@/features/geo-area/components/subscription/GeoAreaSubscription';
import { GroupStep } from '@/features/groups/types/group';
import { AvatarWithMenu } from '@/features/media/components/menu/AvatarWithMenu';
import type { AvatarPickerResult } from '@/features/media/types/picker';
import {
  MeinOrtChannel,
  MeinOrtTab
} from '@/features/mein-ort/components/meinOrtTypes';
import { Alert } from '@/features/popup/components/Alert';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { NavBarType } from '@/navigation/types/navbar';
import type { AppNativeStackScreenProps } from '@/navigation/types/navigation';
import { AppRoute, TabRoute } from '@/navigation/types/navigation';
import { DropdownSelect } from '@/shared/components/DropdownSelect';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';
import { EditMode } from '@/types';

const makeStyles = (theme: AppTheme, insets: EdgeInsets) =>
  StyleSheet.create({
    wrapper: {
      flex: 1,
      backgroundColor: theme.colors.surfaceContainerLow
    },
    contentWrapper: {
      gap: 32,
      paddingHorizontal: 16,
      paddingBottom: 32 + insets.bottom
    },
    header: { alignItems: 'center', gap: 12 },
    image: {
      paddingHorizontal: 48,
      justifyContent: 'center',
      alignItems: 'center'
    },
    // Note: design gap is 16 but it does not include the labels
    inputs: { gap: 8, paddingBottom: 24 },
    input: { backgroundColor: theme.colors.surfaceContainerLow },
    descriptionInput: { minHeight: 180, maxHeight: 180 },
    buttons: { gap: 24 }
  });

type Props = AppNativeStackScreenProps<typeof AppRoute.GroupEditor>;

export const GroupEditorScreen: FC<Props> = ({ route, navigation }) => {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(theme, insets), [insets, theme]);

  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'screens.group-editor'
  });

  const { isFeatureEnabled } = useFeature();
  const isPrivateGroupEnabled = isFeatureEnabled(FeaturesEnum.GroupsPrivate);

  const { groupId } = route.params ?? {};
  const { data: group } = useGetGroup(groupId);
  const { data: person } = useGetPerson();
  const { data: selectedGeoAreas } = useGetSelectedGeoAreas();
  const selectedGeoAreaIds = useMemo(
    () =>
      selectedGeoAreas
        ?.filter(({ id, leaf }) => leaf === true && id !== person?.homeAreaId)
        ?.map(({ id }) => id) ?? [],
    [person?.homeAreaId, selectedGeoAreas]
  );
  const mode = group ? EditMode.EDIT : EditMode.CREATE;

  const [step, setStep] = useState<GroupStep>(GroupStep.GROUP);
  const isInitialStep = step === undefined || step === GroupStep.GROUP;

  const [name, setName] = useState<string>(group?.name ?? '');
  const [description, setDescription] = useState<string>(
    group?.description ?? ''
  );
  const [groupAccessibility, setGroupAccessibility] =
    useState<ClientGroupAccessibilityEnum>(
      group?.accessibility ?? ClientGroupAccessibilityEnum.Public
    );

  const [avatarResult, setAvatarResult] = useState<AvatarPickerResult>({
    temporaryMediaItemId: undefined,
    logo: undefined
  });

  const handleBack = useCallback(
    () => (isInitialStep ? navigation.pop() : setStep(GroupStep.GROUP)),
    [isInitialStep, navigation]
  );

  const { mutate: createGroup, isPending: isCreatePending } = useCreateGroup();
  const { mutate: changeGroup, isPending: isChangePending } = useChangeGroup();
  const { mutate: deleteGroup, isPending: isDeletePending } = useDeleteGroup();

  useEffect(() => {
    navigation.setOptions({
      header: () => (
        <NavigationBar
          disableBackButton={isCreatePending || isChangePending}
          type={
            isInitialStep
              ? NavBarType.MODAL_WITH_CLOSE
              : NavBarType.MODAL_WITH_BACK
          }
          onBackButtonPress={handleBack}
        />
      )
    });
  }, [
    handleBack,
    isChangePending,
    isCreatePending,
    isInitialStep,
    navigation,
    theme.colors.background
  ]);

  const handleCreateOrEditGroup = useCallback(
    (mainGeoAreaId?: string, includedGeoAreaIds: string[] = []) => {
      if (isEmptyOrNull(mainGeoAreaId)) return;

      const localGroup: ClientGroupCreateRequest | ClientGroupChangeRequest = {
        name,
        description,
        groupAccessibility,
        mainGeoAreaId,
        includedGeoAreaIds: [mainGeoAreaId, ...includedGeoAreaIds],
        groupContentVisibility:
          ClientGroupCreateRequestGroupContentVisibilityEnum.Members,
        groupVisibility:
          ClientGroupCreateRequestGroupVisibilityEnum.SameHomeArea
      };

      if (mode === EditMode.CREATE) {
        createGroup(
          {
            ...localGroup,
            logoTemporaryMediaItemId: avatarResult.temporaryMediaItemId
          },
          {
            onSuccess: ({ createdGroup }) =>
              navigation?.replace(AppRoute.GroupDetail, {
                groupId: createdGroup.id,
                name: createdGroup.name,
                shortName: createdGroup.shortName,
                memberCount: createdGroup.memberCount
              })
          }
        );
      } else {
        if (isEmptyOrNull(groupId)) return;

        changeGroup(
          { ...localGroup, groupId, logo: avatarResult.logo },
          {
            onSuccess: ({ updatedGroup }) =>
              navigation?.popTo(AppRoute.GroupDetail, {
                groupId: updatedGroup.id,
                name: updatedGroup.name,
                shortName: updatedGroup.shortName,
                memberCount: updatedGroup.memberCount
              })
          }
        );
      }
    },
    [
      avatarResult.logo,
      avatarResult.temporaryMediaItemId,
      changeGroup,
      createGroup,
      description,
      groupAccessibility,
      groupId,
      mode,
      name,
      navigation
    ]
  );

  const [showDeleteAlert, setShowDeleteAlert] = useState<boolean>(false);

  const handleDeleteGroup = useCallback(() => {
    if (isEmptyOrNull(groupId)) return;
    deleteGroup(
      { groupId },
      {
        onSuccess: () =>
          navigation?.popTo(AppRoute.Tabs, {
            screen: TabRoute.Radio,
            params: {
              channel: MeinOrtChannel.GRUPPEN,
              tab: MeinOrtTab.GRUPPEN
            }
          })
      }
    );
  }, [deleteGroup, groupId, navigation]);

  return isInitialStep ? (
    <>
      {showDeleteAlert && (
        <Portal>
          <Alert
            isVisible
            labels={t('alerts.delete', {
              returnObjects: true
            })}
            dismissable={!isDeletePending}
            onClose={() => setShowDeleteAlert(false)}
            confirmColor={theme.colors.error}
            isConfirmLoading={isDeletePending}
            dismissOnConfirm={false}
            onConfirm={handleDeleteGroup}
          />
        </Portal>
      )}

      <KeyboardAwareScrollView
        style={styles.wrapper}
        contentContainerStyle={styles.contentWrapper}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.header}>
          <Text variant="headlineLarge">{t(`header.${mode}`)}</Text>
        </View>

        <View style={styles.image}>
          <AvatarWithMenu mediaItem={group?.logo} onChange={setAvatarResult} />
        </View>
        <View style={styles.inputs}>
          <TextInput
            editable={true}
            mode="outlined"
            accessibilityLabel={t('name')}
            accessibilityHint={t('name')}
            label={t('name')}
            value={name}
            maxLength={50}
            onChangeText={setName}
            style={styles.input}
          />

          <TextInput
            multiline
            editable={true}
            mode="outlined"
            accessibilityLabel={t('description')}
            accessibilityHint={t('description')}
            textAlignVertical="top"
            placeholder={t('descriptionHint')}
            label={t('description')}
            value={description}
            onChangeText={setDescription}
            maxLength={400}
            style={[styles.input, styles.descriptionInput]}
          />

          {isPrivateGroupEnabled && (
            <DropdownSelect<ClientGroupAccessibilityEnum>
              label={t('access.title')}
              placeholder={t('access.title')}
              selectedOption={{
                value: groupAccessibility,
                label: t(`access.${groupAccessibility}`)
              }}
              options={Object.values(ClientGroupAccessibilityEnum).map(
                (value) => ({
                  label: t(`access.${value}`),
                  value
                })
              )}
              onSelect={setGroupAccessibility}
            />
          )}
        </View>
        <View style={styles.buttons}>
          <Button
            disabled={isEmptyOrNull(name)}
            mode="contained"
            onPress={() => setStep('geo-area')}
          >
            {t('button.next')}
          </Button>
          {mode === EditMode.EDIT && (
            <Button
              mode="text"
              textColor={theme.colors.error}
              onPress={() => setShowDeleteAlert(true)}
            >
              {t('button.delete')}
            </Button>
          )}
        </View>
      </KeyboardAwareScrollView>
    </>
  ) : (
    <GeoAreaSubscription
      selectedGeoAreaId={group?.mainGeoAreaId ?? person?.homeAreaId}
      selectedGeoAreaIds={group?.geoAreaIds ?? selectedGeoAreaIds}
      onChange={handleCreateOrEditGroup}
      disableButton={isCreatePending || isChangePending}
      labels={{
        ...t('geo-area', { returnObjects: true }),
        button: t(`button.${mode}`)
      }}
    />
  );
};
